import React, { useCallback, useRef, useState } from 'react';
import { Box, Heading, SimpleGrid, useToast } from '@chakra-ui/react';
import { useFormContext } from 'react-hook-form';

import { consultarCep } from '../../../../../services/cep';
import { InputForm } from '../../../../../components/inputs/forms/InputForm';
import { UfDropdown } from './UfDropdown';

/**
 * Endereco estruturado em 7 campos — schema-extension v3 elo 3.
 *
 * Reusado em 10 call sites (conferido por grep em 2026-10-09):
 *
 *   StepEmpresa              endereco_estruturado (constituicao)
 *   StepEmpresa              endereco_estruturado (alteracao, tipo "endereco")
 *   StepEmpresa              filiais.${i}
 *   StepEmpresa              filiais_fechar.${i}  (extincao: `cepSoCompleta`)
 *   StepEmpresa              filiais_alterar.${i}       (endereco de hoje: `cepSoCompleta`)
 *   StepEmpresa              filiais_alterar.${i}.novo  (endereco novo)
 *   StepEmpresarioIndividual dados_ei.endereco_estruturado
 *   StepEmpresarioIndividual dados_ei.sede_estruturada
 *   StepSocios               socios.${i}.endereco_estruturado        (PF ou PJ)
 *   StepSocios               socios.${i}.representante.endereco_estruturado
 *
 * Se voce for mexer aqui, RECONTE em vez de confiar nesta lista.
 *
 * Backend (`IEnderecoEstruturado` em `contrato_societario.py`) recebe via
 * `model_validator(mode='before')` que desempacota o sub-objeto nos 7
 * campos flat do banco (M1/M3). Os limites da tela (`LIMITE`) sao o menor
 * entre a coluna, o schema de entrada e o `Endereco` do motor v3: o sub-objeto
 * sai do payload antes da validacao, entao o que passa daqui estoura no banco.
 *
 * ⚠️ O docstring antigo dizia "renderizado SO no branch `!isAlteracao`".
 * ENVELHECEU: `StepSocios.tsx:758` renderiza em ALTERACAO tambem, quando o
 * socio e' admitido (`isNovoSocio`) ou e' continuante que marcou o aspecto
 * 'endereco'. So' o continuante que NAO marcou fica com o textarea legacy.
 *
 * ## Busca de CEP — ligada em todo lugar (user, 2026-10-08)
 *
 * De 28/08 a 08/10 ficou desligada em alteracao, para nao escrever por cima do
 * endereco do vigente. Mas em alteracao este formulario so' aparece para
 * endereco NOVO (filial, sede nova, socio admitido, socio ou representante
 * marcado em "Alterar"); o endereco do vigente e' mostrado em texto, fora daqui.
 *
 * Excecao: a filial ENCERRADA e o endereco DE HOJE da filial que muda. Ele e' o que consta na Junta, e a grafia do
 * ViaCEP por cima ("T 63" x "T-63", rua renomeada) iria para a clausula. La' a busca so'
 * preenche campo VAZIO (`cepSoCompleta`).
 *
 * Fontes: ViaCEP, com a BrasilAPI de reserva (`services/cep.ts`).
 */

export const LIMITE = { logradouro: 200, numero: 20, complemento: 100, bairro: 100, cidade: 100 };

interface EnderecoFormProps {
  basePath: string;
  label?: string;
  isRequired?: boolean;
  /** Alteração: endereço de quadra e lote não tem número, e o contrato sai sem ele. */
  numeroOpcional?: boolean;
  /** Endereço que já existe (filial encerrada): o CEP só preenche campo vazio. */
  cepSoCompleta?: boolean;
}

export const EnderecoForm = ({
  basePath,
  label = 'Endereço',
  isRequired,
  numeroOpcional,
  cepSoCompleta,
}: EnderecoFormProps) => {
  const { setValue, getValues } = useFormContext();
  const toast = useToast();
  const [buscando, setBuscando] = useState(false);
  // Guarda o último CEP consultado: o `onInputChange` dispara a cada tecla, e
  // sem isto uma correção no meio do número refaria a busca do mesmo CEP.
  const ultimoBuscado = useRef<string | null>(null);
  // Numero da consulta em curso. Com a reserva a espera passa de 10 s: CEP
  // trocado nesse meio-tempo nao pode receber o endereco do CEP anterior.
  const consultaAtual = useRef(0);
  // Onde este formulário está AGORA: numa lista (filiais, sócios) o caminho muda quando um
  // item acima é removido, e a resposta do CEP tem de ir para onde o item foi.
  const caminho = useRef(basePath);
  caminho.current = basePath;

  const buscarCep = useCallback(
    async (cepDigitado: string) => {
      const cep = (cepDigitado || '').replace(/\D/g, '');
      if (cep.length !== 8) {
        // Voltou a ficar incompleto: libera pra buscar de novo quando fechar e
        // descarta a consulta que ainda estiver a caminho.
        ultimoBuscado.current = null;
        consultaAtual.current += 1;
        setBuscando(false);
        return;
      }
      if (ultimoBuscado.current === cep) return;
      ultimoBuscado.current = cep;

      consultaAtual.current += 1;
      const consulta = consultaAtual.current;
      // A resposta só vale se quem está no caminho ainda tem ESTE CEP: item removido da
      // lista não tem mais (escrever recriava o item); passo fechado e reaberto, tem.
      const destinoAindaTemEsteCep = () =>
        String(getValues(`${caminho.current}.cep`) ?? '').replace(/\D/g, '') === cep;
      setBuscando(true);
      try {
        const dados = await consultarCep(cep);
        if (consulta !== consultaAtual.current || !destinoAindaTemEsteCep()) return;
        if (!dados) {
          toast({
            status: 'warning',
            title: 'CEP não encontrado',
            description: 'Confira o número ou preencha o endereço à mão.',
            duration: 4000,
          });
          return;
        }

        // O CEP manda (decisão do user): sobrescreve o que estiver preenchido.
        // MAS só escreve o que a resposta de fato traz — CEP genérico de cidade
        // volta com `logradouro` e `bairro` VAZIOS, e gravar a string vazia
        // apagaria o que o operador já tinha digitado. Aí ele preenche cidade e
        // UF e deixa o resto como está.
        const escrever = (campo: string, valor?: string) => {
          if (!valor) return;
          const digitado = String(getValues(`${caminho.current}.${campo}`) ?? '').trim();
          if (cepSoCompleta && digitado) return;
          setValue(`${caminho.current}.${campo}`, valor, {
            shouldDirty: true,
            shouldValidate: true,
          });
        };
        escrever('logradouro', dados.logradouro);
        escrever('bairro', dados.bairro);
        escrever('cidade', dados.cidade);
        escrever('uf', dados.uf); // as duas fontes devolvem a sigla, que é o que o UfDropdown espera

        // `numero` e `complemento` nunca vêm do CEP e não são tocados.
      } catch {
        if (consulta !== consultaAtual.current || !destinoAindaTemEsteCep()) return;
        // Falha de rede ou das duas fontes não pode travar o preenchimento
        // manual — é uma conveniência, não um passo obrigatório do formulário.
        toast({
          status: 'info',
          title: 'Não consegui consultar o CEP',
          description: 'Preencha o endereço à mão; o campo continua válido.',
          duration: 4000,
        });
        ultimoBuscado.current = null;
      } finally {
        if (consulta === consultaAtual.current) setBuscando(false);
      }
    },
    [cepSoCompleta, getValues, setValue, toast],
  );

  return (
    <Box>
      <Heading size="sm" mb={3}>
        {label}
      </Heading>
      <SimpleGrid columns={{ base: 1, md: 12 }} gap={3}>
        {/* Linha 1: CEP primeiro — ele preenche logradouro/bairro/cidade/UF, então
            vem antes do que preenche. Antes ficava no fim da linha, depois dos
            campos que ele mesmo resolve. */}
        <Box gridColumn={{ md: 'span 3' }}>
          <InputForm
            name={`${basePath}.cep`}
            label="CEP"
            mask="99999-999"
            isRequired={isRequired}
            isLoading={buscando}
            controlMb={0}
            helper={cepSoCompleta ? 'Completa o que estiver vazio' : 'Preenche o endereço'}
            onInputChange={valor => buscarCep(String(valor ?? ''))}
          />
        </Box>
        <Box gridColumn={{ md: 'span 7' }}>
          <InputForm
            name={`${basePath}.logradouro`}
            label="Logradouro"
            isRequired={isRequired}
            maxLength={LIMITE.logradouro}
            controlMb={0}
          />
        </Box>
        <Box gridColumn={{ md: 'span 2' }}>
          <InputForm
            name={`${basePath}.numero`}
            label="Número"
            isRequired={isRequired && !numeroOpcional}
            maxLength={LIMITE.numero}
            controlMb={0}
            helper={numeroOpcional ? 'Vazio se não houver' : "Ou 's/n'"}
          />
        </Box>

        {/* Linha 2: complemento (opcional, full-width) */}
        <Box gridColumn={{ md: 'span 12' }}>
          <InputForm
            name={`${basePath}.complemento`}
            label="Complemento"
            helper="Opcional — ex: 'Apto 1500', 'Bloco B'"
            maxLength={LIMITE.complemento}
            controlMb={0}
          />
        </Box>

        {/* Linha 3: bairro + cidade + UF */}
        <Box gridColumn={{ md: 'span 5' }}>
          <InputForm
            name={`${basePath}.bairro`}
            label="Bairro"
            isRequired={isRequired}
            maxLength={LIMITE.bairro}
            controlMb={0}
          />
        </Box>
        <Box gridColumn={{ md: 'span 5' }}>
          <InputForm
            name={`${basePath}.cidade`}
            label="Cidade"
            isRequired={isRequired}
            maxLength={LIMITE.cidade}
            controlMb={0}
          />
        </Box>
        <Box gridColumn={{ md: 'span 2' }}>
          <UfDropdown name={`${basePath}.uf`} label="UF" isRequired={isRequired} controlMb={0} />
        </Box>
      </SimpleGrid>
    </Box>
  );
};
